import { useEffect, useId, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { createAlterationSchema, parseRupees, type Alteration } from '@tailoring-crm/shared';
import { isApiRequestError } from '../../lib/api/errors.js';
import { useCreateAlteration } from '../../lib/alterations/use-alterations.js';
import { useTemplates } from '../../lib/measurements/use-templates.js';
import { CustomerPicker, type PickedCustomer } from '../customers/customer-picker.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
  Label,
  useToast,
} from '@tailoring-crm/ui';

/**
 * Booking a walk-in: a garment this shop did not stitch, brought in only to be altered.
 *
 * The same conversation as booking one back from an order — what is wrong, when they can have
 * it, what it costs — with the two things an order would have answered asked first: WHO it is
 * (found in the customer book, or added there on the spot) and WHAT the garment is (the shop's
 * own garment types, or "Other…" typed as the customer said it).
 *
 * The charge is still last and still blank-means-free, for the reason the order-side dialog
 * gives: the counter should understand the problem before it decides about money.
 */

/** The select's value for "not one of ours — type it". Not a garment type key. */
const OTHER = '__other__';

export interface WalkInAlterationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Handed the booked alteration, so the page can open it straight away. */
  onBooked?: (alteration: Alteration) => void;
}

export function WalkInAlterationDialog({
  open,
  onOpenChange,
  onBooked,
}: WalkInAlterationDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fieldId = useId();
  const create = useCreateAlteration();
  const templates = useTemplates({ enabled: open });
  const garmentTypes = (templates.data?.templates ?? []).filter((template) => template.active);

  const [customer, setCustomer] = useState<PickedCustomer | null>(null);
  const [garment, setGarment] = useState('');
  const [otherLabel, setOtherLabel] = useState('');
  const [complaint, setComplaint] = useState('');
  const [promisedDate, setPromisedDate] = useState('');
  const [charge, setCharge] = useState('');
  const [notes, setNotes] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  // Opening it again starts a fresh booking, not the last customer's half-typed one.
  useEffect(() => {
    if (!open) return;
    setCustomer(null);
    setGarment('');
    setOtherLabel('');
    setComplaint('');
    setPromisedDate('');
    setCharge('');
    setNotes('');
    setFieldErrors({});
    setFormError(null);
  }, [open]);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);

    let chargePaise = 0;
    try {
      // Blank means free, which is what most re-fits are.
      chargePaise = charge.trim() === '' ? 0 : parseRupees(charge);
    } catch {
      setFieldErrors({ chargePaise: t('alterations.walkIn.chargeInvalid') });
      return;
    }

    const payload = {
      ...(customer ? { customerId: customer.id } : {}),
      ...(garment === OTHER
        ? { garmentTypeLabel: otherLabel }
        : garment
          ? { garmentTypeKey: garment }
          : {}),
      complaint,
      promisedDate: promisedDate || null,
      chargePaise,
      notes: notes.trim() === '' ? null : notes,
    };

    const parsed = createAlterationSchema.safeParse(payload);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[issue.path.join('.')] ??= issue.message;
      setFieldErrors(next);
      return;
    }

    try {
      const booked = await create.mutateAsync(payload);
      toast({
        title: t('alterations.walkIn.booked', { number: booked.number }),
        variant: 'success',
      });
      onOpenChange(false);
      onBooked?.(booked);
    } catch (caught) {
      setFormError(isApiRequestError(caught) ? caught.message : t('error.unexpected'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('alterations.walkIn.title')}</DialogTitle>
          <DialogDescription>{t('alterations.walkIn.description')}</DialogDescription>
        </DialogHeader>

        <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)} noValidate>
          {formError ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
            >
              {formError}
            </p>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <CustomerPicker value={customer} onChange={setCustomer} disabled={create.isPending} />
            {fieldErrors.customerId ? (
              <p className="text-sm text-destructive">{fieldErrors.customerId}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-garment`}>{t('alterations.walkIn.garment')}</Label>
            <select
              id={`${fieldId}-garment`}
              value={garment}
              onChange={(event) => setGarment(event.target.value)}
              aria-invalid={fieldErrors.garmentTypeLabel ? true : undefined}
              className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
            >
              <option value="">{t('alterations.walkIn.garmentPlaceholder')}</option>
              {garmentTypes.map((template) => (
                <option key={template.garmentTypeKey} value={template.garmentTypeKey}>
                  {template.garmentTypeLabel}
                </option>
              ))}
              <option value={OTHER}>{t('alterations.walkIn.garmentOther')}</option>
            </select>
            {garment === OTHER ? (
              <Input
                aria-label={t('alterations.walkIn.garmentOtherLabel')}
                value={otherLabel}
                onChange={(event) => setOtherLabel(event.target.value)}
                placeholder={t('alterations.walkIn.garmentOtherPlaceholder')}
                aria-invalid={fieldErrors.garmentTypeLabel ? true : undefined}
              />
            ) : null}
            {fieldErrors.garmentTypeLabel ? (
              <p className="text-sm text-destructive">{fieldErrors.garmentTypeLabel}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-complaint`}>{t('alterations.field.complaint')}</Label>
            <Input
              id={`${fieldId}-complaint`}
              value={complaint}
              onChange={(event) => setComplaint(event.target.value)}
              placeholder={t('alterations.field.complaintPlaceholder')}
              aria-invalid={fieldErrors.complaint ? true : undefined}
            />
            {fieldErrors.complaint ? (
              <p className="text-sm text-destructive">{fieldErrors.complaint}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-promised`}>{t('alterations.field.promisedDate')}</Label>
              <Input
                id={`${fieldId}-promised`}
                type="date"
                value={promisedDate}
                onChange={(event) => setPromisedDate(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-charge`}>{t('alterations.field.charge')}</Label>
              <Input
                id={`${fieldId}-charge`}
                inputMode="decimal"
                value={charge}
                onChange={(event) => setCharge(event.target.value)}
                placeholder={t('alterations.field.chargePlaceholder')}
                aria-invalid={fieldErrors.chargePaise ? true : undefined}
              />
              {fieldErrors.chargePaise ? (
                <p className="text-sm text-destructive">{fieldErrors.chargePaise}</p>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-notes`}>{t('alterations.field.notes')}</Label>
            <Textarea
              id={`${fieldId}-notes`}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={create.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {t('alterations.bookAction')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default WalkInAlterationDialog;
